import { forwardRef, useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import ReactSelect from 'react-select';
import { SelectProps } from './select.types';
import '@/assets/styles/components/select.scss';
import clsx from 'clsx';
import { useSizes } from '@/utility/responsive.ts';
import { If } from 'vite-plugin-react-control-statements';

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
	(
		{
			label,
			error,
			options,
			placeholder = 'Seçin',
			title,
			variant = 'default',
			selectedValues = [],
			onMultipleChange,
			maxSelection = 3,
			value,
			className = '',
			disabled = false,
			alwaysShowPlaceholder = false,
			hiddenMultipleSaveButton = false,
			onChange,
			isSortSelect = false,
			...props
		},
		ref
	) => {
		const { isDesktop } = useSizes();
		const [isOpen, setIsOpen] = useState(false);
		const selectRef = useRef<HTMLDivElement>(null);

		useEffect(() => {
			const handleClickOutside = (event: MouseEvent) => {
				if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
					setIsOpen(false);
				}
			};

			document.addEventListener('mousedown', handleClickOutside);
			return () => document.removeEventListener('mousedown', handleClickOutside);
		}, []);

		const handleMultipleSelect = (optionValue: string) => {
			if (!onMultipleChange) return;

			let newSelected: string[];
			if (selectedValues?.includes(optionValue)) {
				newSelected = selectedValues?.filter(v => v !== optionValue);
			} else {
				if (selectedValues?.length >= maxSelection) return;
				newSelected = [...selectedValues, optionValue];
			}
			onMultipleChange(newSelected);
		};

		const getSelectedLabels = () => {
			return selectedValues
				?.map(value => options?.find(opt => opt.value === value)?.label)
				.filter(Boolean)
				.join(', ');
		};

		const onMultiple = () => {
			if (disabled) {
				return;
			}
			setIsOpen(!isOpen);
		};

		if (variant === 'multiple') {
			return (
				<div
					className="select-field"
					ref={selectRef}>
					<div
						className="select-trigger"
						onClick={onMultiple}>
						<span
							className={clsx('select-value', {
								placeholder: !selectedValues?.length
							})}>
							{alwaysShowPlaceholder ? placeholder : selectedValues?.length > 0 ? getSelectedLabels() : placeholder}
						</span>
						<ChevronDown className={isOpen ? 'rotate' : ''} />
					</div>

					{isOpen && (
						<div className="select-dropdown">
							{!!title && (
								<div className="dropdown-header">
									<h4>{title}</h4>
								</div>
							)}
							<div className="options-grid">
								{options?.map(option => (
									<div
										key={option.value}
										className={`option-item ${selectedValues?.includes(option.value) ? 'selected' : ''} ${
											selectedValues?.length >= maxSelection && !selectedValues?.includes(option.value) ? 'disabled' : ''
										}`}
										onClick={() => handleMultipleSelect(option.value)}>
										<input
											type="checkbox"
											value={option.value}
											checked={selectedValues?.includes(option.value)}
										/>
										<div className="checkbox-mark"></div>
										<span>{option.label}</span>
									</div>
								))}
							</div>
							{!hiddenMultipleSaveButton && (
								<div className="dropdown-footer">
									<button
										type="button"
										onClick={() => setIsOpen(false)}
										disabled={selectedValues?.length === 0}>
										Seçimleri Kaydet
									</button>
								</div>
							)}
						</div>
					)}
				</div>
			);
		}

		const selectOptions =
			options?.map(opt => ({
				value: opt.value,
				label: opt.label
			})) || [];

		const selectedOption = selectOptions.find(opt => opt.value === value) || null;

		const handleSelectChange = (selectedOption: any) => {
			if (onChange) {
				const syntheticEvent = {
					target: {
						value: selectedOption?.value || '',
						name: props.name || ''
					}
				} as React.ChangeEvent<HTMLSelectElement>;

				onChange(syntheticEvent);
			}
		};

		const customStyles = {
			control: (provided: any) =>
				isSortSelect
					? {
							...provided,
							border: 'none',
							width: '28px',
							height: '28px',
							minHeight: '28px',
							borderRadius: 0,
							background: 'transparent'
						}
					: {
							...provided,
							fontFamily: 'Manrope, serif',
							minWidth: isDesktop ? '154px' : 'auto',
							display: 'flex',
							padding: '17px 15px',
							justifyContent: 'center',
							alignItems: 'flex-start',
							width: '100%',
							borderRadius: '10px',
							background: 'var(--surface-subtle)',
							border: 'none',
							outline: 'none',
							color: 'var(--text-primary)',
							fontWeight: '700',
							fontSize: '16px',
							cursor: 'pointer',
							minHeight: '56px',
							height: '56px',
							boxShadow: 'none',
							'&:hover': {
								border: 'none'
							}
						},
			valueContainer: (provided: any) => ({
				...provided,
				padding: '0',
				margin: '0'
			}),
			placeholder: (provided: any) => ({
				...provided,
				color: 'var(--text-secondary)',
				fontWeight: '400',
				margin: '0'
			}),
			singleValue: (provided: any) => ({
				...provided,
				color: 'var(--text-primary)',
				fontWeight: '700',
				margin: '0',
				width: 'calc(100% - 25px)'
			}),
			indicatorSeparator: () => ({
				display: 'none'
			}),
			dropdownIndicator: () => ({
				display: 'none'
			}),
			menu: (provided: any) => ({
				...provided,
				borderRadius: '10px',
				marginTop: '4px',
				background: 'var(--surface-f8fafc)',
				border: 'none',
				boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
				minWidth: isSortSelect ? '280px' : 'initial'
			}),
			menuList: (provided: any) => ({
				...provided,
				padding: '0'
			}),
			option: (provided: any, state: any) =>
				isSortSelect
					? {
							...provided,
							color: state.isSelected ? 'var(--text-primary)' : 'var(--text-secondary)',
							backgroundColor: state.isSelected ? 'var(--surface-e5f3ff)' : 'transparent',
							fontWeight: state.isSelected ? '700' : '400',
							fontSize: '12px',
							padding: '9px',
							cursor: 'pointer',
							textAlign: 'left',
							'&:hover': {
								backgroundColor: 'var(--surface-e5f3ff)',
								color: 'var(--text-primary)'
							}
						}
					: {
							...provided,
							color: state.isSelected ? 'var(--text-primary)' : 'var(--text-secondary)',
							backgroundColor: state.isSelected ? 'var(--surface-e5f3ff)' : 'transparent',
							fontWeight: state.isSelected ? '700' : '400',
							fontSize: '16px',
							padding: '17px 15px',
							cursor: 'pointer',
							'&:hover': {
								backgroundColor: 'var(--surface-e5f3ff)',
								color: 'var(--text-primary)'
							}
						},
			noOptionsMessage: (provided: any) => ({
				...provided,
				color: 'var(--text-secondary)',
				fontSize: '16px',
				fontWeight: '400',
				padding: '17px 15px',
				textAlign: 'center'
			})
		};

		return (
			<div
				className={clsx('select-field', {
					'custom-control': isSortSelect
				})}
				ref={selectRef}>
				{label && <label className="select-label">{label}</label>}
				<div className="select-wrapper">
					<ReactSelect
						isRtl={isSortSelect}
						ref={ref}
						options={selectOptions}
						value={selectedOption}
						onChange={handleSelectChange}
						placeholder={placeholder}
						isDisabled={disabled}
						isClearable={false}
						isSearchable={false}
						styles={customStyles}
						className={`${error ? 'error' : ''} ${className}`}
						noOptionsMessage={() => 'Seçenek Bulunamadı'}
						{...props}
					/>
					<If condition={!isSortSelect}>
						<ChevronDown className="" />
					</If>
				</div>
				{error && <span className="error-message">{error}</span>}
			</div>
		);
	}
);

Select.displayName = 'Select';
